import * as user from '../actions/user.action';
import {OrganizationUser, SelfOrganizationUser} from '../../../afb-api/models/organization-user';

export interface State {
  self: SelfOrganizationUser;
  selfOrgInfo: OrganizationUser;
  orgUserList: OrganizationUser[];
  isRetrievingUserList: boolean;
  isInvitingOrg: boolean;
  isInvitingGroup: boolean;
  isRemovingFromOrg: boolean;
  isRemovingFromGroup: boolean;
  isOrgAccessSetting: boolean;
  isGroupAccessSetting: boolean;
}

export const initialState: State = {
  self: null,
  selfOrgInfo: null,
  orgUserList: [],
  isRetrievingUserList: false,
  isInvitingOrg: false,
  isInvitingGroup: false,
  isRemovingFromOrg: false,
  isRemovingFromGroup: false,
  isOrgAccessSetting: false,
  isGroupAccessSetting: false
};

/**
 * Because our object state tree is read-only, our response for every action
 * must return a new state object without mutating the previous state object.
 */

export function reducer(state = initialState, action: user.Actions): State {
  switch (action.type) {
    case user.REQUEST_ORG_USER_LIST_COMPLETE: {
      const orgUserList: OrganizationUser[] = <OrganizationUser[]>action.payload;

      return {...state, orgUserList};
    }

    case user.REQUEST_SELF_ORG_INFO_COMPLETE: {
      const selfOrgInfo: OrganizationUser = <OrganizationUser>action.payload;

      return {...state, selfOrgInfo};
    }

    // Start Loading
    case user.START_RETRIEVING_USER_LIST: {
      return {...state, isRetrievingUserList: true};
    }
    case user.START_INVITING_ORG: {
      return {...state, isInvitingOrg: true};
    }
    case user.START_INVITING_GROUP: {
      return {...state, isInvitingGroup: true};
    }
    case user.START_REMOVING_FROM_ORG: {
      return {...state, isRemovingFromOrg: true};
    }
    case user.START_REMOVING_FROM_GROUP: {
      return {...state, isRemovingFromGroup: true};
    }
    case user.START_SETTING_ORG_ACCESS: {
      return {...state, isOrgAccessSetting: true};
    }
    case user.START_SETTING_GROUP_ACCESS: {
      return {...state, isGroupAccessSetting: true};
    }

    // Success
    case user.INVITE_ORG_SUCCESS: {
      return {...state, isInvitingOrg: false};
    }
    case user.INVITE_GROUP_SUCCESS: {
      return {...state, isInvitingGroup: false};
    }
    case user.REMOVE_FROM_ORG_SUCCESS: {
      return {...state, isRemovingFromOrg: false};
    }
    case user.REMOVE_FROM_GROUP_SUCCESS: {
      return {...state, isRemovingFromGroup: false};
    }
    case user.SET_ORG_USER_ACCESS_SUCCESS: {
      return {...state, isOrgAccessSetting: false};
    }
    case user.SET_GROUP_USER_ACCESS_SUCCESS: {
      return {...state, isGroupAccessSetting: false};
    }

    // Fails
    case user.INVITE_ORG_FAIL: {
      console.warn('USER: Invite Org Fail');
      return {...state, isInvitingOrg: false};
    }
    case user.INVITE_GROUP_FAIL: {
      console.warn('USER: Invite Group Fail');
      return {...state, isInvitingGroup: false};
    }
    case user.REMOVE_FROM_ORG_FAIL: {
      console.warn('USER: Remove From Org Fail');
      return {...state, isRemovingFromOrg: false};
    }
    case user.REMOVE_FROM_GROUP_FAIL: {
      console.warn('USER: Remove From Group Fail');
      return {...state, isRemovingFromGroup: false};
    }
    case user.SET_ORG_USER_ACCESS_FAIL: {
      console.warn('USER: Set Org User Access Fail');
      return {...state, isOrgAccessSetting: false};
    }
    case user.SET_GROUP_USER_ACCESS_FAIL: {
      console.warn('USER: Set Group User Access Fail');
      return {...state, isGroupAccessSetting: false};
    }

    default:
      return state;
  }
};

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */

export const getSelf = (state: State) => state.self;
export const getSelfOrgInfo = (state: State) => state.selfOrgInfo;
export const getOrgUserList = (state: State) => state.orgUserList;
// Loading Status
export const getRetrievingStatus = (state: State) => state.isRetrievingUserList;
export const getInvitingOrgStatus = (state: State) => state.isInvitingOrg;
export const getInvitingGroupStatus = (state: State) => state.isInvitingGroup;
export const getRemovingFromOrgStatus = (state: State) => state.isRemovingFromOrg;
export const getRemovingFromGroupStatus = (state: State) => state.isRemovingFromGroup;
export const getOrgAccessSettingStatus = (state: State) => state.isOrgAccessSetting;
export const getGroupAccessSettingStatus = (state: State) => state.isGroupAccessSetting;
